// SHARED per-group programme completeness (CLIENT).
//
// One source of truth for "is this group fully decided?" so the per-group selection section
// (VastKeuzeSection) and the Controleren section (ProgrammaReviewSection) agree to the letter.
// It mirrors VastKeuzeSection's old `groupHasChoice`: a group is resolved when BOTH its vast
// decision (>=1 vaste OR "geen vast") and its keuze decision (>=1 keuze OR keuze-opt-out) are
// made, but only for the dimensions that actually have aanbod for that group. Groups with no
// aanbod at all are auto-resolved.
//
// On top of that, every SELECTED offering must be FULLY ANSWERED (via isOfferingFullyAnswered):
//   - an offering that HAS voorkeursperiode date-blocks needs >= 1 chosen period OR an explicit
//     "geen voorkeur" answer (NOT merely being auto-selected);
//   - an offering that HAS add-ons needs EVERY add-on answered (Ja/Nee).
// This applies to vast AND keuze offerings, so an auto-selected group (e.g. Groep 3/4 with 1 vast
// + 1 keuze) reads "bezig" until those are answered.
//
// It reads the SAME raw /eligible + /selections payloads both sections already fetch/cache, and
// computes eligibility via @/shared/programme-rules so the rule cannot drift from the server's
// notion of who is eligible.
import {
  isOfferingEligibleForGroup,
  isOfferingFullyAnswered,
  type OfferingRules,
} from "@/shared/programme-rules";

// ── Minimal raw-payload shapes (the fields completeness needs) ──────────────────────────────
type EligibleOffering = {
  id: number;
  enrolmentModeCode: string;
  datePreferencePolicyCode: string;
  requiredYearLevels: string[] | null;
  participatingYearLevels: string[] | null;
  maxYearLevels: string[] | null;
  vastStackable: boolean;
};
type OfferingYL = { offeringId: number; yearLevelCode: string };
type GroupYLRow = { groupId: number; displayName: string; yearLevelCode: string };
type DateBlock = { offeringId: number };
type OfferingAddon = { offeringId: number; addonCode: string };

/** The slice of the /eligible payload completeness needs. */
export type EligiblePayload = {
  offerings?: EligibleOffering[];
  offeringYearLevels?: OfferingYL[];
  classGroupYearLevels?: GroupYLRow[];
  blockedOfferingIds?: number[];
  offeringDateBlocks?: DateBlock[];
  offeringAddons?: OfferingAddon[];
};

type Selection = { id: number; offeringId: number };
type SelGroup = { selectionId: number; classGroupId: number };
type OptOut = { classGroupId: number; enrolmentModeCode: string };
// Voorkeur + add-on answers are PER (selection, group): each carries classGroupId.
type TimePreference = { id: number; selectionId: number; classGroupId: number; preferenceKindCode: string };
type TimePrefDateRange = { preferenceId: number };
type SelectionAddon = { selectionId: number; classGroupId: number; addonCode: string };

/** The slice of the /selections payload completeness needs. */
export type SelectionsPayload = {
  selections?: Selection[];
  groups?: SelGroup[];
  optOuts?: OptOut[];
  timePreferences?: TimePreference[];
  timePreferenceDateRanges?: TimePrefDateRange[];
  selectionAddons?: SelectionAddon[];
};

export type GroupCompleteness = {
  groupId: number;
  displayName: string;
  resolved: boolean;
};

export type ProgrammeCompleteness = {
  /** Groups that have ANY vast/keuze aanbod (the ones that must be resolved). */
  groupsToResolve: GroupCompleteness[];
  /** Subset of groupsToResolve that is not yet resolved. */
  unresolvedGroups: GroupCompleteness[];
  /** True when every group that has aanbod is resolved. */
  allResolved: boolean;
};

const ylNum = (code: string) => parseInt(code.replace("groep_", ""), 10) || 99;

function toRules(o: EligibleOffering): OfferingRules {
  return {
    id: o.id,
    enrolmentModeCode: o.enrolmentModeCode,
    requiredYearLevels: o.requiredYearLevels,
    participatingYearLevels: o.participatingYearLevels,
    maxYearLevels: o.maxYearLevels,
    vastStackable: o.vastStackable,
  };
}

/**
 * Compute per-group programme completeness for the vast + keuze dimensions, identical to the
 * rule the per-group selection section uses. `mode` narrows the dimensions exactly like
 * VastKeuzeSection: "vast_keuze" considers vast + keuze, "technohub" considers technohub only
 * (no vast). The Controleren step uses "vast_keuze".
 */
export function computeProgrammeCompleteness(
  eligible: EligiblePayload | null | undefined,
  selections: SelectionsPayload | null | undefined,
  mode: "vast_keuze" | "technohub" = "vast_keuze",
): ProgrammeCompleteness {
  const offerings = eligible?.offerings ?? [];
  const offeringYLs = eligible?.offeringYearLevels ?? [];
  const groupYLs = eligible?.classGroupYearLevels ?? [];
  const blockedSet = new Set(eligible?.blockedOfferingIds ?? []);
  const dateBlockOfferingIds = new Set((eligible?.offeringDateBlocks ?? []).map((b) => b.offeringId));
  // offeringId -> the add-on codes it has (each must be answered when the offering is selected).
  const addonCodesByOffering = new Map<number, string[]>();
  for (const a of eligible?.offeringAddons ?? []) {
    if (!addonCodesByOffering.has(a.offeringId)) addonCodesByOffering.set(a.offeringId, []);
    addonCodesByOffering.get(a.offeringId)!.push(a.addonCode);
  }

  const sels = selections?.selections ?? [];
  const selGroups = selections?.groups ?? [];
  const optOuts = selections?.optOuts ?? [];
  const timePrefs = selections?.timePreferences ?? [];
  const timePrefRanges = selections?.timePreferenceDateRanges ?? [];
  const selectionAddons = selections?.selectionAddons ?? [];

  const modeSet = new Set<string>(mode === "technohub" ? ["technohub"] : ["vast", "keuze"]);
  const optOutMode = mode === "technohub" ? "technohub" : "keuze";

  // Groups (id + displayName + leerjaar codes), sorted by lowest leerjaar then name to keep the
  // "Nog open: ..." list in the same order the school sees its groups.
  const groupMap = new Map<number, { id: number; displayName: string; codes: string[] }>();
  for (const r of groupYLs) {
    if (!groupMap.has(r.groupId)) groupMap.set(r.groupId, { id: r.groupId, displayName: r.displayName, codes: [] });
    groupMap.get(r.groupId)!.codes.push(r.yearLevelCode);
  }
  const groups = [...groupMap.values()].sort(
    (a, b) =>
      Math.min(...a.codes.map(ylNum)) - Math.min(...b.codes.map(ylNum)) ||
      a.displayName.localeCompare(b.displayName, "nl", { numeric: true }),
  );

  // offeringId -> its year_levels (the codes that make it eligible).
  const ylByOffering = new Map<number, string[]>();
  for (const yl of offeringYLs) {
    if (!ylByOffering.has(yl.offeringId)) ylByOffering.set(yl.offeringId, []);
    ylByOffering.get(yl.offeringId)!.push(yl.yearLevelCode);
  }
  const offeringById = new Map(offerings.map((o) => [o.id, o]));
  const selectionByOffering = new Map(sels.map((s) => [s.offeringId, s.id]));
  const groupsBySelection = new Map<number, number[]>();
  for (const sg of selGroups) {
    if (!groupsBySelection.has(sg.selectionId)) groupsBySelection.set(sg.selectionId, []);
    groupsBySelection.get(sg.selectionId)!.push(sg.classGroupId);
  }
  // Answers are PER (selection, group), keyed by `${selectionId}:${classGroupId}`.
  const key = (selId: number, groupId: number) => `${selId}:${groupId}`;
  // (selection, group) -> has a usable "preferred" voorkeur (a stored date-block range) for THAT group.
  const groupHasPreferred = new Set<string>();
  const rangePrefIds = new Set(timePrefRanges.map((r) => r.preferenceId));
  // (selection, group) -> has the explicit "geen voorkeur" marker (a geen_voorkeur kind, no date-range).
  const groupHasGeenVoorkeur = new Set<string>();
  for (const tp of timePrefs) {
    if (tp.preferenceKindCode === "preferred" && rangePrefIds.has(tp.id)) groupHasPreferred.add(key(tp.selectionId, tp.classGroupId));
    if (tp.preferenceKindCode === "geen_voorkeur") groupHasGeenVoorkeur.add(key(tp.selectionId, tp.classGroupId));
  }
  // (selection, group) -> set of answered add-on codes for THAT group (a row exists, Ja OR Nee).
  const answeredAddonsByGroup = new Map<string, Set<string>>();
  for (const sa of selectionAddons) {
    const k = key(sa.selectionId, sa.classGroupId);
    if (!answeredAddonsByGroup.has(k)) answeredAddonsByGroup.set(k, new Set());
    answeredAddonsByGroup.get(k)!.add(sa.addonCode);
  }

  const isSelectedForGroup = (offeringId: number, groupId: number) => {
    const selId = selectionByOffering.get(offeringId);
    return selId != null && (groupsBySelection.get(selId) ?? []).includes(groupId);
  };
  // Whether a SELECTED offering is fully answered FOR THIS GROUP (voorkeursperiode + add-ons), via
  // the shared rule. Reads only THAT group's answers.
  const offeringFullyAnsweredForGroup = (offeringId: number, groupId: number) => {
    const selId = selectionByOffering.get(offeringId);
    if (selId == null) return true; // not selected -> nothing to answer
    const k = key(selId, groupId);
    return isOfferingFullyAnswered(
      { hasDateBlocks: dateBlockOfferingIds.has(offeringId), addonCodes: addonCodesByOffering.get(offeringId) ?? [] },
      {
        hasPreferredPeriod: groupHasPreferred.has(k),
        hasGeenVoorkeur: groupHasGeenVoorkeur.has(k),
        answeredAddonCodes: answeredAddonsByGroup.get(k) ?? new Set(),
      },
    );
  };

  const eligibleOfferings = (codes: string[]) =>
    offerings.filter(
      (o) => modeSet.has(o.enrolmentModeCode) && isOfferingEligibleForGroup(toRules(o), ylByOffering.get(o.id) ?? [], codes, blockedSet.has(o.id)),
    );

  const groupsToResolve: GroupCompleteness[] = [];
  for (const g of groups) {
    const eligible = eligibleOfferings(g.codes);
    if (eligible.length === 0) continue; // no aanbod -> auto-resolved, not in the must-resolve set

    const vast = eligible.filter((o) => o.enrolmentModeCode === "vast");
    const keuze = eligible.filter((o) => o.enrolmentModeCode !== "vast");

    const isGeenVast = optOuts.some((o) => o.classGroupId === g.id && o.enrolmentModeCode === "vast");
    const isSectionOptedOut = optOuts.some((o) => o.classGroupId === g.id && o.enrolmentModeCode === optOutMode);

    const vastDecided = isGeenVast || vast.some((o) => isSelectedForGroup(o.id, g.id));
    const keuzeDecided = isSectionOptedOut || keuze.some((o) => isSelectedForGroup(o.id, g.id));

    const vastOk = vast.length === 0 || vastDecided;
    const keuzeOk = keuze.length === 0 || keuzeDecided;
    // Every offering SELECTED for this group must be fully answered FOR THIS GROUP: a selected
    // offering with voorkeursperiode date-blocks needs >= 1 chosen period OR an explicit "geen
    // voorkeur" FOR THIS GROUP, and a selected offering with add-ons needs every add-on answered FOR
    // THIS GROUP. Applies to vast AND keuze, so an auto-selected group stays unresolved until those
    // answers are given for that group.
    const answersMissing = eligible.some((o) => isSelectedForGroup(o.id, g.id) && !offeringFullyAnsweredForGroup(o.id, g.id));

    const resolved = vastOk && keuzeOk && !answersMissing;
    groupsToResolve.push({ groupId: g.id, displayName: g.displayName, resolved });
  }

  const unresolvedGroups = groupsToResolve.filter((g) => !g.resolved);
  return { groupsToResolve, unresolvedGroups, allResolved: unresolvedGroups.length === 0 };
}
